/**
 * Working Hours editor — /me surface for per-advisor slot windows.
 *
 * Lets an advisor view + edit the windows that drive their appointment
 * slot generation (advisors.custom_working_hours via
 * /api/me-working-hours). Defaults to 9–5 ET Mon–Fri until customized.
 *
 * Why this exists: advisors had no way to open evening/weekend slots —
 * the 9–5 window was hard-coded in slot generation, so clearing Google
 * Calendar for a 7 PM meeting could never surface a 7 PM slot.
 *
 * Notes:
 *  - Google Calendar free/busy + confirmed-booking checks still apply
 *    inside any window — widening hours can't cause double-bookings.
 *  - Admins can edit any advisor's hours via /me preview mode (the
 *    endpoint accepts advisorId for admins).
 *  - Accounts without an advisor record (SDR / staff) never see this
 *    component — /me gates it on a loaded advisor.
 */

import { useEffect, useState } from 'react';
import { authedFetch } from '../lib/authed-fetch';

interface WorkBlock {
  start: string;
  end: string;
}
type WeekSchedule = Record<string, WorkBlock[]>;

interface WorkingHoursEditorProps {
  advisorId: string;
  /** True when the signed-in user is viewing their own /me. */
  isSelf: boolean;
  advisorFirstName: string;
}

const DAY_LABELS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const MAX_BLOCKS_PER_DAY = 3;

const TIME_OPTIONS: string[] = (() => {
  const out: string[] = [];
  for (let h = 6; h <= 21; h++) {
    out.push(`${String(h).padStart(2, '0')}:00`);
    out.push(`${String(h).padStart(2, '0')}:30`);
  }
  out.push('22:00');
  return out;
})();

function formatTimeLabel(t: string): string {
  const [hStr, mStr] = t.split(':');
  const h = Number(hStr);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${mStr} ${suffix}`;
}

function emptySchedule(): WeekSchedule {
  return { '0': [], '1': [], '2': [], '3': [], '4': [], '5': [], '6': [] };
}

export function WorkingHoursEditor({
  advisorId,
  isSelf,
  advisorFirstName,
}: WorkingHoursEditorProps) {
  const [schedule, setSchedule] = useState<WeekSchedule | null>(null);
  const [isCustom, setIsCustom] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [bufferMinutes, setBufferMinutes] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setSchedule(null);
    setLoadError(null);
    setSaveMsg(null);
    setSaveError(null);
    setDirty(false);
    authedFetch(`/api/me-working-hours?advisorId=${encodeURIComponent(advisorId)}`)
      .then(async (r) => {
        const payload = (await r.json().catch(() => null)) as
          | { ok: true; weekSchedule: WeekSchedule; isCustom: boolean; bufferMinutes: number }
          | { ok: false; message: string }
          | null;
        if (cancelled) return;
        if (!payload || !payload.ok) {
          setLoadError(payload && !payload.ok ? payload.message : 'Network error');
          return;
        }
        setSchedule({ ...emptySchedule(), ...payload.weekSchedule });
        setIsCustom(payload.isCustom);
        setBufferMinutes(payload.bufferMinutes ?? 0);
      })
      .catch(() => {
        if (!cancelled) setLoadError('Network error');
      });
    return () => {
      cancelled = true;
    };
  }, [advisorId]);

  function mutate(updater: (prev: WeekSchedule) => WeekSchedule) {
    setSchedule((prev) => (prev ? updater(prev) : prev));
    setDirty(true);
    setSaveMsg(null);
    setSaveError(null);
  }

  function toggleDay(day: string, enabled: boolean) {
    mutate((prev) => ({
      ...prev,
      [day]: enabled ? [{ start: '09:00', end: '17:00' }] : [],
    }));
  }

  function setBlockField(day: string, idx: number, field: 'start' | 'end', value: string) {
    mutate((prev) => ({
      ...prev,
      [day]: prev[day].map((b, i) => (i === idx ? { ...b, [field]: value } : b)),
    }));
  }

  function addBlock(day: string) {
    mutate((prev) => {
      const blocks = prev[day];
      if (blocks.length >= MAX_BLOCKS_PER_DAY) return prev;
      const lastEnd = blocks.length > 0 ? blocks[blocks.length - 1].end : '17:00';
      const startIdx = Math.min(TIME_OPTIONS.indexOf(lastEnd) + 2, TIME_OPTIONS.length - 3);
      const start = startIdx >= 0 ? TIME_OPTIONS[startIdx] : '18:00';
      const endIdx = Math.min(TIME_OPTIONS.indexOf(start) + 4, TIME_OPTIONS.length - 1);
      return {
        ...prev,
        [day]: [...blocks, { start, end: TIME_OPTIONS[endIdx] }],
      };
    });
  }

  function removeBlock(day: string, idx: number) {
    mutate((prev) => ({
      ...prev,
      [day]: prev[day].filter((_, i) => i !== idx),
    }));
  }

  function validateLocal(s: WeekSchedule): string | null {
    for (let d = 0; d <= 6; d++) {
      const blocks = s[String(d)] ?? [];
      const sorted = [...blocks].sort((a, b) => a.start.localeCompare(b.start));
      for (const b of sorted) {
        if (b.start >= b.end) {
          return `${DAY_LABELS[d]}: a window's start time must be before its end time.`;
        }
      }
      for (let i = 1; i < sorted.length; i++) {
        if (sorted[i].start < sorted[i - 1].end) {
          return `${DAY_LABELS[d]}: windows overlap.`;
        }
      }
    }
    return null;
  }

  function save(resetToDefault: boolean) {
    if (!schedule && !resetToDefault) return;
    if (!resetToDefault && schedule) {
      const problem = validateLocal(schedule);
      if (problem) {
        setSaveError(problem);
        return;
      }
    }
    setSaving(true);
    setSaveMsg(null);
    setSaveError(null);
    authedFetch('/api/me-working-hours', {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        advisorId,
        weekSchedule: resetToDefault ? null : schedule,
        bufferMinutes,
      }),
    })
      .then(async (r) => {
        const payload = (await r.json().catch(() => null)) as
          | { ok: true; weekSchedule: WeekSchedule; isCustom: boolean; bufferMinutes: number }
          | { ok: false; message: string }
          | null;
        if (!payload || !payload.ok) {
          setSaveError(payload && !payload.ok ? payload.message : 'Save failed — try again.');
          return;
        }
        setSchedule({ ...emptySchedule(), ...payload.weekSchedule });
        setIsCustom(payload.isCustom);
        setBufferMinutes(payload.bufferMinutes ?? 0);
        setDirty(false);
        setSaveMsg(
          resetToDefault
            ? 'Reset to the default 9 AM – 5 PM, Monday–Friday.'
            : 'Saved. New windows are live on the next availability load.'
        );
      })
      .catch(() => setSaveError('Save failed — try again.'))
      .finally(() => setSaving(false));
  }

  const whose = isSelf ? 'Your' : `${advisorFirstName}'s`;

  return (
    <section className="admin-card">
      <h2>{whose} working hours</h2>
      <p className="admin-card-desc">
        These windows control which appointment slots prospects can book
        {isSelf ? ' with you' : ` with ${advisorFirstName}`} — including
        evenings and weekends if you add them. All times are Eastern.
        Google Calendar busy times and existing bookings are always
        respected inside any window, so widening hours never risks a
        double-booking.
        {!isCustom && ' Currently using the default: 9 AM – 5 PM, Monday–Friday.'}
      </p>

      {loadError && (
        <p className="admin-card-desc" style={{ color: '#B42318' }}>
          Couldn't load working hours: {loadError}. Try refreshing.
        </p>
      )}

      {!schedule && !loadError && <p className="admin-card-desc">Loading…</p>}

      {schedule && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {DAY_LABELS.map((label, d) => {
            const key = String(d);
            const blocks = schedule[key] ?? [];
            const enabled = blocks.length > 0;
            return (
              <div
                key={key}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  padding: '6px 0',
                  borderBottom: d < 6 ? '1px solid #ECECEC' : 'none',
                }}
              >
                <label
                  style={{
                    width: 130,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    paddingTop: 4,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(e) => toggleDay(key, e.target.checked)}
                  />
                  <strong>{label}</strong>
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                  {!enabled && (
                    <span style={{ color: '#8C8C8C', paddingTop: 4 }}>Unavailable</span>
                  )}
                  {blocks.map((b, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <select
                        value={b.start}
                        onChange={(e) => setBlockField(key, i, 'start', e.target.value)}
                      >
                        {TIME_OPTIONS.map((t) => (
                          <option key={t} value={t}>
                            {formatTimeLabel(t)}
                          </option>
                        ))}
                      </select>
                      <span>to</span>
                      <select
                        value={b.end}
                        onChange={(e) => setBlockField(key, i, 'end', e.target.value)}
                      >
                        {TIME_OPTIONS.map((t) => (
                          <option key={t} value={t}>
                            {formatTimeLabel(t)}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="admin-copy-btn admin-copy-btn-secondary"
                        onClick={() => removeBlock(key, i)}
                        aria-label={`Remove ${label} window ${i + 1}`}
                      >
                        Remove
                      </button>
                      {i === blocks.length - 1 && blocks.length < MAX_BLOCKS_PER_DAY && (
                        <button
                          type="button"
                          className="admin-copy-btn admin-copy-btn-secondary"
                          onClick={() => addBlock(key)}
                        >
                          + Add window
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginTop: 12,
              paddingTop: 12,
              borderTop: '1px solid #ECECEC',
            }}
          >
            <label htmlFor="buffer-select" style={{ fontWeight: 600, minWidth: 200 }}>
              Buffer between meetings
            </label>
            <select
              id="buffer-select"
              value={bufferMinutes}
              onChange={(e) => {
                setBufferMinutes(Number(e.target.value));
                setDirty(true);
                setSaveMsg(null);
                setSaveError(null);
              }}
            >
              <option value={0}>Off (no buffer)</option>
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
            </select>
            <span style={{ color: '#8C8C8C', flex: 1, minWidth: 220 }}>
              Time padded before and after every booked meeting so a prospect
              cannot land a slot the instant another meeting ends.
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
            <button
              type="button"
              className="bob-primary-btn"
              disabled={saving || !dirty}
              onClick={() => save(false)}
            >
              {saving ? 'Saving…' : 'Save working hours'}
            </button>
            <button
              type="button"
              className="admin-copy-btn admin-copy-btn-secondary"
              disabled={saving}
              onClick={() => save(true)}
            >
              Reset to default (9–5 Mon–Fri)
            </button>
            {saveMsg && <span style={{ color: '#067647' }}>{saveMsg}</span>}
            {saveError && <span style={{ color: '#B42318' }}>{saveError}</span>}
          </div>
        </div>
      )}

      <p className="admin-card-note">
        Example: to open Thursday evenings, add a second Thursday window
        like 6:00 PM to 8:00 PM — prospects will then see a 7:00 PM slot
        when the calendar is free.
      </p>
    </section>
  );
}

